import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { animeList, banners, categories } from '@/mocks/anime';
import type { AnimeItem } from '@/mocks/anime';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs ${star <= Math.round(rating / 2) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
        ></i>
      ))}
      <span className="text-xs text-foreground-500 ml-1">{rating}</span>
    </div>
  );
}

function AnimeCard({ anime }: { anime: AnimeItem }) {
  return (
    <Link
      to={`/anime/${anime.id}`}
      className="group block cursor-pointer"
      data-product-shop
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
        <img
          src={anime.coverImage}
          alt={`${anime.title} - 51动漫网在线观看`}
          title={`${anime.title} - 51动漫在线观看免费全集`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {anime.isHot && (
            <span className="px-2 py-0.5 bg-primary-500 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
              热门
            </span>
          )}
          {anime.isNew && (
            <span className="px-2 py-0.5 bg-accent-500 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
              新番
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 bg-foreground-900/70 text-white text-[10px] font-medium rounded-full whitespace-nowrap">
            {anime.rating}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-900/80 to-transparent p-3">
          <span className="text-white text-[10px] font-medium">
            {anime.status === '连载中' ? `更新至${anime.episodes}话` : `共${anime.episodes}话`}
          </span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
        {anime.title}
      </h3>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[10px] px-1.5 py-0.5 bg-secondary-100 text-secondary-800 rounded whitespace-nowrap">
          {anime.category}
        </span>
        <span className="text-xs text-foreground-500">{anime.views}</span>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotAnimes = animeList.filter((a) => a.isHot).slice(0, 8);
  const newAnimes = animeList.filter((a) => a.isNew).slice(0, 8);
  const jpAnimes = animeList.filter((a) => a.category === '日漫').slice(0, 6);
  const cnAnimes = animeList.filter((a) => a.category === '国漫').slice(0, 6);
  const krAnimes = animeList.filter((a) => a.category === '韩漫').slice(0, 6);
  const rankedAnimes = [...animeList].sort((a, b) => b.rating - a.rating).slice(0, 10);

  const faqItems = [
    { q: '51动漫网需要注册才能观看吗？', a: '不需要！51动漫网完全免费，无需注册即可在线观看全部动漫内容，支持高清全集追更与下拉式阅读。' },
    { q: '51动漫网支持哪些类型的动漫？', a: '51动漫网涵盖日漫、韩漫、国漫三大类型，包含热血、奇幻、修仙、恋爱、搞笑等各种题材，应有尽有。' },
    { q: '什么是下拉式阅读？', a: '下拉式阅读是51动漫网独创的阅读体验，您只需向下滚动页面即可连续追更，无需频繁翻页，追漫更流畅。' },
    { q: '51动漫网的内容更新频率如何？', a: '51动漫网每日更新，与各大平台同步追更，热门动漫第一时间上线，确保您不错过任何精彩内容。' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '51动漫网 - 51动漫在线观看免费全集',
            description: '51动漫网提供51动漫在线观看服务，支持高清全集免费追更与下拉式阅读，日漫韩漫国漫一应俱全。',
            url: 'https://www.51dongman.com/',
            isPartOf: { '@type': 'WebSite', name: '51动漫网', url: 'https://www.51dongman.com/' },
          }),
        }}
      />

      <section className="relative h-[500px] md:h-[620px] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${index === currentBanner ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={banner.image} alt={banner.title} className="w-full h-full object-cover object-top" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/30 to-background-50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
          <p className="text-accent-200 text-sm md:text-base font-medium mb-3 tracking-widest">✨ 欢迎来到</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-4 drop-shadow-lg">
            51动漫网
          </h1>
          <p className="text-white/80 text-sm md:text-lg max-w-2xl leading-relaxed mb-8">
            高清免费追更日漫韩漫国漫 · 下拉式流畅阅读体验 · 全网热门动漫一网打尽
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/category/日漫"
              className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              立即追更
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/20"
            >
              了解更多
            </Link>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentBanner(index)}
              className={`w-8 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentBanner ? 'bg-primary-500 w-12' : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`切换到第${index + 1}张`}
            />
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-16 relative z-20" data-product-shop>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat}
              to={`/category/${cat}`}
              className="group bg-background-50 rounded-xl p-6 flex items-center gap-4 hover:shadow-md transition-all duration-300 cursor-pointer border border-background-200/70"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-primary-50 group-hover:bg-primary-100 transition-colors duration-300 flex-shrink-0">
                <i
                  className={`text-2xl md:text-3xl text-primary-500 ${
                    cat === '日漫' ? 'ri-sun-line' : cat === '韩漫' ? 'ri-moon-line' : 'ri-fire-line'
                  }`}
                ></i>
              </div>
              <div>
                <h3 className="font-heading text-lg text-foreground-900 group-hover:text-primary-500 transition-colors duration-200">
                  {cat}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">
                  {cat === '日漫' ? '热血·冒险·经典日系' : cat === '韩漫' ? '剧情·奇幻·韩系精品' : '修仙·玄幻·国风盛宴'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">热门推荐</h2>
            <p className="text-sm text-foreground-500 mt-2">全网最热动漫，大家都在追</p>
          </div>
          <Link to="/category/日漫" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer">
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
          {hotAnimes.map((anime) => (
            <AnimeCard key={anime.id} anime={anime} />
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">日漫精选</h2>
              <p className="text-sm text-foreground-500 mt-2">经典日系动漫，热血与感动并存</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {jpAnimes.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">国漫精选</h2>
            <p className="text-sm text-foreground-500 mt-2">国风动漫崛起，修仙玄幻精彩纷呈</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
          {cnAnimes.map((anime) => (
            <AnimeCard key={anime.id} anime={anime} />
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">韩漫精选</h2>
              <p className="text-sm text-foreground-500 mt-2">韩系精品漫画，剧情与画风双在线</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {krAnimes.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">最新上架</h2>
                <p className="text-sm text-foreground-500 mt-2">新番速递，第一时间尝鲜</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {newAnimes.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">评分排行</h2>
                <p className="text-sm text-foreground-500 mt-2">高分神作，口碑之选</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5">
              {rankedAnimes.map((anime, index) => (
                <Link
                  key={anime.id}
                  to={`/anime/${anime.id}`}
                  className="flex items-center gap-4 py-3 border-b border-background-200/50 last:border-b-0 hover:bg-background-100/50 rounded-lg px-2 -mx-2 transition-colors duration-200 cursor-pointer"
                >
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                      index < 3 ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <img
                    src={anime.coverImage}
                    alt={anime.title}
                    className="w-10 h-14 rounded object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground-900 line-clamp-1">{anime.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 bg-secondary-100 text-secondary-800 rounded whitespace-nowrap">
                        {anime.category}
                      </span>
                      <StarRating rating={anime.rating} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">常见问题</h2>
            <p className="text-sm text-foreground-500 mt-2">关于51动漫网，您想了解的都在这</p>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-800">{item.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          }),
        }}
      />
    </>
  );
}